import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Clock,
  Fingerprint,
  Plane,
  RefreshCw,
  Sun,
  Users,
} from "lucide-react";
import { getHrmDashboard } from "../../api/hrm/dashboard";
import { getAttendanceStatus } from "../../api/hrm/employees/attendanceLog";
import { hasPermission } from "../../utils/permissionUtils";
import {
  filterHubShortcuts,
  getHrmHubItems,
  HUB_PREVIEW_LIMIT,
} from "./mobileNavConfig";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileStatCard,
  MobileShortcutCard,
} from "./MobileNavPrimitives";

function fmtHours(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}h`;
}

function fmtDays(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}n`;
}

const SHORTCUT_TONES = {
  branches: "sky",
  plans: "amber",
  business_trip: "teal",
  my_payslip: "emerald",
  hrm_documents: "indigo",
  signing: "rose",
  overtime: "amber",
  on_leave: "rose",
  hrm_attendance: "emerald",
  employees: "violet",
};

const KPI_KEY_BY_CARD = {
  attendance: "hrm_attendance",
  overtime: "overtime",
  leave: "on_leave",
  employees: "employees",
};

export default function HrmHubScreen() {
  const navigate = useNavigate();
  const hubItems = useMemo(() => getHrmHubItems(), []);

  const canDash = hasPermission("hrm.dashboard", "R");
  const canAttendance = hasPermission("hrm.attendance", "R");
  const canOvertime = hasPermission("hrm.overtime", "R");
  const canOnLeave = hasPermission("hrm.on_leave", "R");
  const canEmployees = hasPermission("hrm.employees", "R");
  const canBusinessTrip = hasPermission("hrm.business_trip", "R");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [attendanceHint, setAttendanceHint] = useState(null);

  const load = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      let nextStats = null;
      let nextRecent = [];
      let nextHint = null;
      const jobs = [];

      if (canDash) {
        const now = new Date();
        jobs.push(
          getHrmDashboard({
            year: now.getFullYear(),
            month: now.getMonth() + 1,
            branch_id: "all",
          })
            .then((data) => {
              nextStats = data?.stats || null;
              nextRecent = Array.isArray(data?.recentLeaveBusiness)
                ? data.recentLeaveBusiness.slice(0, HUB_PREVIEW_LIMIT)
                : [];
            })
            .catch(() => {})
        );
      }

      if (canAttendance) {
        jobs.push(
          getAttendanceStatus()
            .then((data) => {
              nextHint = data?.message || null;
            })
            .catch(() => {})
        );
      }

      await Promise.all(jobs);
      setStats(nextStats);
      setRecent(nextRecent);
      setAttendanceHint(nextHint);
      setLoading(false);
      setRefreshing(false);
    },
    [canDash, canAttendance]
  );

  useEffect(() => {
    load();
  }, [load]);

  const kpiCards = useMemo(() => {
    const cards = [];
    const s = stats;
    if (!s && !canDash) return cards;

    if (canAttendance && (canDash ? s : true)) {
      cards.push({
        key: "attendance",
        label: "Chấm công tháng",
        value: s ? fmtHours(s.attendanceHoursMonth) : "—",
        hint:
          s?.attendancePeopleMonth != null
            ? `${s.attendancePeopleMonth} người`
            : attendanceHint || "Mở chấm công",
        icon: Clock,
        tone: "emerald",
        href: "/hrm/hrm_attendance",
      });
    }
    if (canOvertime && s) {
      cards.push({
        key: "overtime",
        label: "Tăng ca tháng",
        value: fmtHours(s.overtimeHoursMonth),
        hint:
          s.overtimePeopleMonth != null
            ? `${s.overtimePeopleMonth} người`
            : "Giờ OT",
        icon: Clock,
        tone: "amber",
        href: "/hrm/overtime",
      });
    }
    if ((canOnLeave || canBusinessTrip) && s) {
      cards.push({
        key: "leave",
        label: "Nghỉ & công tác",
        value: fmtDays(s.leaveTripDaysMonth),
        hint:
          s.leaveTripPeopleMonth != null
            ? `${s.leaveTripPeopleMonth} người`
            : "Ngày trong tháng",
        icon: Sun,
        tone: "rose",
        href: canOnLeave ? "/hrm/on_leave" : "/hrm/business_trip",
      });
    }
    if (canEmployees && s?.employees) {
      cards.push({
        key: "employees",
        label: "Nhân viên",
        value: String(s.employees.active ?? s.employees.total ?? "—"),
        hint:
          s.employees.total != null
            ? `${s.employees.total} tổng`
            : "Đang hoạt động",
        icon: Users,
        tone: "violet",
        href: "/hrm/employees",
      });
    }

    return cards.slice(0, 4);
  }, [
    stats,
    canDash,
    canAttendance,
    canOvertime,
    canOnLeave,
    canBusinessTrip,
    canEmployees,
    attendanceHint,
  ]);

  const shortcuts = useMemo(() => {
    const exclude = kpiCards
      .map((c) => KPI_KEY_BY_CARD[c.key])
      .filter(Boolean);
    if (canOnLeave) exclude.push("business_trip");
    return filterHubShortcuts(hubItems, exclude);
  }, [hubItems, kpiCards, canOnLeave]);

  const openRecent = (row) => {
    const isLeave = row?.type === "Nghỉ phép";
    navigate(isLeave ? "/hrm/on_leave" : "/hrm/business_trip");
  };

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="HRM"
        subtitle="Nhân sự, chấm công, nghỉ phép…"
        tone="violet"
        trailing={
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-700 active:bg-violet-100 disabled:opacity-50"
            aria-label="Làm mới"
          >
            <RefreshCw
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {(canDash || kpiCards.length > 0) && (
          <section>
            <MobileSectionHeader title="Tình hình HRM" tone="violet" />
            {loading ? (
              <div className="grid grid-cols-2 gap-3">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-[100px] animate-pulse rounded-2xl bg-violet-100/50"
                  />
                ))}
              </div>
            ) : kpiCards.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {kpiCards.map((card) => (
                  <MobileStatCard
                    key={card.key}
                    label={card.label}
                    value={card.value}
                    hint={card.hint}
                    icon={card.icon}
                    tone={card.tone}
                    onClick={() => navigate(card.href)}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/40 px-4 py-8 text-center text-[12px] text-gray-400">
                Chưa có chỉ số HRM
              </p>
            )}
          </section>
        )}

        {canAttendance && attendanceHint ? (
          <section>
            <MobileSectionHeader title="Cần bạn xử lý" tone="rose" />
            <button
              type="button"
              onClick={() => navigate("/hrm/hrm_attendance")}
              className="flex w-full items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50/80 px-3.5 py-3 text-left active:bg-rose-100/70"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-100 text-rose-700">
                <Fingerprint size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-rose-800/80">
                  Chấm công hôm nay
                </p>
                <p className="truncate text-sm font-medium text-gray-800">
                  {attendanceHint}
                </p>
              </div>
              <ChevronRight size={16} className="shrink-0 text-rose-300" />
            </button>
          </section>
        ) : null}

        {shortcuts.length > 0 ? (
          <section>
            <MobileSectionHeader title="Lối tắt" tone="indigo" />
            <div className="grid grid-cols-2 gap-3">
              {shortcuts.map((item) => (
                <MobileShortcutCard
                  key={item.key}
                  icon={item.icon}
                  label={item.label}
                  tone={SHORTCUT_TONES[item.key] || "violet"}
                  onClick={() => navigate(`/hrm/${item.key}`)}
                />
              ))}
            </div>
          </section>
        ) : null}

        {recent.length > 0 ? (
          <section>
            <MobileSectionHeader
              title="Nghỉ phép & công tác gần đây"
              tone="amber"
              action={
                canOnLeave ? (
                  <button
                    type="button"
                    onClick={() => navigate("/hrm/on_leave")}
                    className="text-[12px] font-medium text-amber-700"
                  >
                    Xem tất cả
                  </button>
                ) : null
              }
            />
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-50">
              {recent.map((row) => {
                const isLeave = row.type === "Nghỉ phép";
                return (
                  <button
                    key={`${row.type}-${row.id}`}
                    type="button"
                    onClick={() => openRecent(row)}
                    className="flex w-full items-start gap-3 px-4 py-3.5 text-left active:bg-gray-50"
                  >
                    <div
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                        isLeave
                          ? "bg-rose-50 text-rose-700"
                          : "bg-sky-50 text-sky-700"
                      }`}
                    >
                      {isLeave ? <Sun size={16} /> : <Plane size={16} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-800">
                        {row.employee_name || "—"}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-gray-400">
                        {[row.type, row.day_count != null ? `${row.day_count} ngày` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <ChevronRight
                      size={14}
                      className="mt-2 shrink-0 text-gray-300"
                    />
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {!loading &&
        kpiCards.length === 0 &&
        shortcuts.length === 0 &&
        recent.length === 0 ? (
          <p className="py-12 text-center text-[12px] text-gray-400">
            Không có chức năng HRM khả dụng
          </p>
        ) : null}
      </div>
    </MobilePageShell>
  );
}
